import type { OnSelectContextCallback } from "@/components/ai/prompt-input/context-selector-menu/ContextSelectorMenu";
import { contextAssociatedProjectsAtom } from "@/components/ai/prompt-input/state";
import { ErrorMessage } from "@/components/ErrorMessage";
import { useListProjectEnvironmentClassEntries, useListProjects, type PlainProject } from "@/queries/project-queries";
import { CommandGroup, CommandItem, CommandList, CommandEmpty } from "@/components/flexkit/Command";
import { CommandLoading } from "cmdk";
import { atom, useAtom, useAtomValue, useSetAtom } from "jotai";
import type React from "react";
import { memo, useEffect } from "react";
import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import { formatError } from "@/utils/errors";
import { useToast } from "@/components/podkit/toasts/use-toast";

const hoveredProjectAtom = atom<PlainProject | null>(null);
const selectedProjectAtom = atom<PlainProject | null>(null);
const isLoadingEnvClassesAtom = atom(false);

export const ProjectMenu = memo(({ onSelectContext }: { onSelectContext: OnSelectContextCallback }) => {
    const contextAssociatedProjects = useAtomValue(contextAssociatedProjectsAtom);
    const {
        data: allProjects,
        isLoading: isLoadingProjects,
        error,
    } = useListProjects({
        enabled: contextAssociatedProjects.length === 0,
    });
    const selectableProjects = contextAssociatedProjects.length > 0 ? contextAssociatedProjects : allProjects?.projects;
    const [selectedProject, setSelectedProject] = useAtom(selectedProjectAtom);
    const setHoveredProject = useSetAtom(hoveredProjectAtom);
    const isLoadingEnvClasses = useAtomValue(isLoadingEnvClassesAtom);

    if (error) {
        return <ErrorMessage error={error} />;
    }

    let content: React.ReactNode = null;
    if (isLoadingProjects) {
        content = (
            <>
                <CommandLoading className="sr-only">Loading projects…</CommandLoading>
                {[1, 2, 3].map((index) => (
                    <div key={`skeleton-${index}`} className="w-full rounded px-2 py-1 last:pb-2">
                        <div className="h-6 w-full animate-pulse rounded bg-surface-muted" />
                    </div>
                ))}
            </>
        );
    } else if (!!selectableProjects && selectableProjects.length > 0) {
        content = (
            <CommandGroup value="projects">
                {selectableProjects.map((project) => (
                    <CommandItem
                        key={project.id}
                        value={project.metadata?.name || project.id}
                        disabled={isLoadingEnvClasses}
                        onMouseEnter={() => {
                            setHoveredProject(project);
                        }}
                        onSelect={() => {
                            setHoveredProject(project);
                            setSelectedProject(project);
                        }}
                        className="w-full cursor-pointer px-3 py-1 text-start"
                    >
                        <span>{project.metadata?.name || project.id}</span>
                        {isLoadingEnvClasses && project.id === selectedProject?.id && (
                            <IconSpinner className="ml-auto animate-spin" size="sm" />
                        )}
                    </CommandItem>
                ))}
            </CommandGroup>
        );
    } else {
        content = null;
    }

    return (
        <ProjectMenuCommandList onSelectContext={onSelectContext}>
            {isLoadingProjects ? null : <CommandEmpty className="px-3 py-2">No projects found</CommandEmpty>}
            {content}
        </ProjectMenuCommandList>
    );
});
ProjectMenu.displayName = "ProjectMenu";

const ProjectMenuCommandList = memo<React.PropsWithChildren<{ onSelectContext: OnSelectContextCallback }>>(
    ({ onSelectContext, children }) => {
        const hoveredProject = useAtomValue(hoveredProjectAtom);
        const [selectedProject, setSelectedProject] = useAtom(selectedProjectAtom);
        const setIsLoadingEnvClasses = useSetAtom(isLoadingEnvClassesAtom);
        const {
            data: projectEnvironmentClassEntries,
            isLoading,
            error,
        } = useListProjectEnvironmentClassEntries({
            projectId: hoveredProject?.id,
        });
        const { toast } = useToast();

        useEffect(() => {
            if (selectedProject) {
                setIsLoadingEnvClasses(isLoading);
            }
        }, [setIsLoadingEnvClasses, selectedProject, isLoading]);

        useEffect(() => {
            if (selectedProject && error) {
                toast({
                    title: "Failed to load environment classes for the selected project",
                    description: formatError(error),
                });
            }
        }, [selectedProject, error, toast]);

        useEffect(() => {
            if (
                selectedProject &&
                projectEnvironmentClassEntries &&
                projectEnvironmentClassEntries.projectId === selectedProject.id
            ) {
                onSelectContext(
                    [
                        {
                            kind: "project",
                            project: selectedProject,
                            environmentClasses: projectEnvironmentClassEntries.environmentClasses,
                        },
                    ],
                    [],
                );
                setSelectedProject(null);
            }
        }, [selectedProject, projectEnvironmentClassEntries, setSelectedProject, onSelectContext]);

        return <CommandList className="max-h-40 w-full overflow-auto">{children}</CommandList>;
    },
);
ProjectMenuCommandList.displayName = "ProjectMenuCommandList";
